import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import { getApiErrorStatus } from '@/utils/apiError'

/** 404/405/501 = rota ainda não deployada; o web deploya antes do backend. */
const BACKEND_WITHOUT_ROUTE_STATUSES = [404, 405, 501]

/**
 * Corpo de `POST /effective-check/recompute` (F1 §6.2 / Q25).
 *
 * `dryRun` é `true` por default NO BACKEND, e o web nunca deve depender disso:
 * a tela do backoffice manda o valor explícito nas duas etapas. Aplicar exige
 * o `confirmToken` que o dry-run anterior devolveu (TTL de 30 min).
 */
export interface RecomputeEffectiveCheckPayload {
  /** Data local do plantão, `YYYY-MM-DD` — nunca um instante UTC. */
  startDate: string
  /** `YYYY-MM-DD`, inclusiva. */
  endDate: string
  scheduleIds?: number[]
  healthUnitIds?: number[]
  /**
   * Inclui congelados NO RELATÓRIO. Eles nunca são recalculados: congelado é
   * decisão humana do pós-plantão e o comando administrativo não a desfaz.
   */
  includeFrozen?: boolean
  /** `true` simula e devolve o `confirmToken`; `false` aplica e exige o token. */
  dryRun?: boolean
  /** SHA-256 hexadecimal devolvido pelo dry-run. Obrigatório quando `dryRun: false`. */
  confirmToken?: string
}

export interface EffectiveCheckDiffScope {
  startDate: string
  endDate: string
  scheduleIds?: number[]
  healthUnitIds?: number[]
  includeFrozen: boolean
}

export interface EffectiveCheckDiffByChange {
  checkinChanged: number
  checkoutChanged: number
  sourceChanged: number
  statusChanged: number
  /** Congelados pulados — contados, nunca recalculados. */
  frozenSkipped: number
}

export interface EffectiveCheckDiffMinutes {
  deltaCheckinTotal: number
  deltaCheckoutTotal: number
  deltaWorkedTotal: number
  deltaWorkedP90: number
}

/** Uma linha do top de dinheiro. Reais como STRING com 2 casas — nunca `number`. */
export interface EffectiveCheckMoneyTop {
  doctorStationId: number
  before: string
  after: string
  delta: string
}

export interface EffectiveCheckDiffMoney {
  deltaTotalBRL: string
  stationsWithMoneyDelta: number
  /** Plantões sem regra de pagamento resolvível: não somam no total. */
  unresolved: number
  top: EffectiveCheckMoneyTop[]
}

export interface EffectiveCheckDiffSample {
  doctorStationId: number
  before: Record<string, unknown> | null
  after: Record<string, unknown>
  /**
   * F4-FX2 — plantão congelado pelo pós-plantão. Entra no relatório com
   * `includeFrozen`, mas o "Aplicar" o PULA: sem esta marca a tabela mostrava um
   * "Depois" e um Δ que nunca seriam gravados. Opcional porque um binário
   * anterior à FX2 não a manda.
   */
  frozen?: boolean
}

/**
 * M5-1 — plantão já antecipado/pago NUNCA é bloqueado nem excluído do
 * recálculo; só aparece aqui para a Operação avisar o financeiro. Bloquear
 * deixaria o horário efetivo errado para sempre justamente no plantão que já
 * virou dinheiro.
 */
export interface EffectiveCheckDiffFlags {
  alreadyPaidOrAnticipated: number
  /** Até 50 ids, para conferência manual. */
  doctorStationIds: number[]
}

export interface EffectiveCheckDiffReport {
  scope: EffectiveCheckDiffScope
  stationsEvaluated: number
  stationsChanged: number
  byChange: EffectiveCheckDiffByChange
  minutes: EffectiveCheckDiffMinutes
  money: EffectiveCheckDiffMoney
  /** Top 50 por |Δ minutos|. */
  samples: EffectiveCheckDiffSample[]
  flags: EffectiveCheckDiffFlags
  /** Avisos operacionais — nunca bloqueiam. */
  warnings: string[]
  applied: boolean
  /** Plantões efetivamente recalculados (0 em dry-run). */
  stationsRecomputed: number
  /** Só em dry-run; é ele que autoriza a aplicação seguinte. */
  confirmToken?: string
}

/**
 * Comando administrativo do horário efetivo (F1 §6.2).
 *
 * Endpoint e não script porque `src/scripts/` está fora do build do backend: em
 * PROD não existe `ts-node` nem os fontes. Permissão
 * `attendance_general_administrating` — a F1 §6.2 venceu o `effective_check_recalculating`
 * do mockup M5 (§7.5 do spec F2–F4 é explícito).
 */
export default class EffectiveCheckService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/effective-check'
  }

  /**
   * Simula (`dryRun: true`) ou aplica (`dryRun: false` + `confirmToken`) o
   * recálculo do período.
   *
   * Devolve `null` em 404/405/501: a rota é da F1 e pode não estar no binário
   * do ambiente — a tela do backoffice mostra "recurso indisponível" em vez de
   * um toast de erro. Os erros que importam continuam subindo com a mensagem
   * do backend: 400 (período/escopo/token ausente), 403 (sem a permissão) e
   * **409 (token expirado ou o mundo mudou desde o dry-run)** — este último é o
   * que impede aplicar um relatório velho, e engoli-lo seria perigoso.
   */
  async recompute(
    payload: RecomputeEffectiveCheckPayload
  ): Promise<EffectiveCheckDiffReport | null> {
    try {
      const { data } = await request.post<EffectiveCheckDiffReport>(
        this.getCompletePath('/recompute'),
        payload
      )
      return data ?? null
    } catch (e: unknown) {
      if (BACKEND_WITHOUT_ROUTE_STATUSES.includes(getApiErrorStatus(e) ?? 0)) {
        console.debug('[EffectiveCheck] rota de recálculo indisponível neste ambiente', e)
        return null
      }
      throw this.handleError(e, 'Não foi possível recalcular o horário efetivo')
    }
  }
}
